import type { QueryClient } from "@tanstack/react-query";
import { chatTokenKeys } from "Api/Queries/Chat/useChatTokenQuery";
import { blockedKeys } from "Api/Queries/Social/useBlockedUsersQuery";
import { disclosedNamesKeys } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { followsKeys } from "Api/Queries/Social/useFollowListsQuery";
import { followRequestsKeys } from "Api/Queries/Social/useFollowRequestsQuery";
import { mutualFollowersKeys } from "Api/Queries/Social/useMutualFollowersQuery";
import { publicProfileKeys } from "Api/Queries/Social/usePublicProfileQuery";
import { relationshipKeys } from "Api/Queries/Social/useRelationshipQuery";
import { socialUserSearchKeys } from "Api/Queries/Social/useSocialUserSearchQuery";
import { storiesTokenKeys } from "Api/Queries/Stories/useStoriesTokenQuery";

/**
 * Every graph mutation can change lists AND the relationship capabilities (e.g. approving a
 * request creates a follow, blocking severs both follows), so they all reconcile the same query
 * families. Profiles and search are included because a mutual follow changes what they
 * disclose: it makes a private profile behave like a public one (name, bio, name-searchability)
 * — and a block hides the pair from each other.
 *
 * Read families only, so it is safe on BOTH sides of a change: the actor's mutation and the
 * passive side's hub push (useSocialGraphSignalR). It costs our API one page per mounted family
 * and nothing on Stream.
 */
export const invalidateSocialGraph = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: mutualFollowersKeys.all });
  queryClient.invalidateQueries({ queryKey: disclosedNamesKeys.all });
  queryClient.invalidateQueries({ queryKey: followRequestsKeys.all });
  queryClient.invalidateQueries({ queryKey: followsKeys.all });
  queryClient.invalidateQueries({ queryKey: blockedKeys.all });
  queryClient.invalidateQueries({ queryKey: relationshipKeys.all });
  queryClient.invalidateQueries({ queryKey: publicProfileKeys.all });
  queryClient.invalidateQueries({ queryKey: socialUserSearchKeys.all });
};

/**
 * The chat token fetch is the backstop of DM promotion (App ADR-0010): it upserts the Stream
 * profile and sweeps the caller's mutual-follow DMs that Stream missed. That is a Stream write on
 * every fetch, so it is NOT part of invalidateSocialGraph — an unfollow, block or deleted request
 * cannot complete a pair and must not cost a Stream call per click. Only the mutations that can
 * complete one call this: follow and approve when their result says the pair is now mutual, and
 * going public, whose bulk approvals are promoted by this sweep alone. The passive side
 * (useSocialGraphSignalR) never calls it — the command that completed the pair already promoted
 * the DM server-side.
 */
export const invalidateChatTokenForMutualFollow = (
  queryClient: QueryClient,
) => {
  queryClient.invalidateQueries({ queryKey: chatTokenKeys.all });
  // Same for stories: the token fetch is where the backend ensures the viewer's timeline follows
  // the authors they follow (ADR-0002). A follow/unfollow/block changes that set, and the open
  // timeline re-reads itself when the new token arrives (useStoriesFeed) — without this, a newly
  // followed merchant's stories showed up only after the token's 30-minute staleTime or a re-login.
  queryClient.invalidateQueries({ queryKey: storiesTokenKeys.all });
};
